import { useState, useEffect, useCallback } from "react"
import AdminProductCard from "./Products/AdminProductCard"
import NewProductModal from "./Products/NewProductModal"
import EditProductModal from "./Products/EditProductModal"
import AddProductsModal from "./Products/AddProductsModal"

function ProductsPanel() {
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [currentProduct, setCurrentProduct] = useState(null)
  const [updated, setUpdated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectedProducts, setSelectedProducts] = useState([])
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  const [merchants, setMerchants] = useState([])
  const [selectedMerchant, setSelectedMerchant] = useState("")

  // Pagination
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const refresh = () => setUpdated((prev) => !prev)

  /* =========================
     FETCH PRODUCTS (UPDATED)
  ========================= */
  const getProducts = useCallback(async () => {
    try {
      setLoading(true)

      const url = new URL(`${process.env.REACT_APP_API_URL}/products`)
      url.searchParams.append("page", page)
      url.searchParams.append("limit", 10)

      if (selectedMerchant) {
        url.searchParams.append("merchant", selectedMerchant)
      }

      if (search) {
        if (debouncedSearch) {
          url.searchParams.append("search", debouncedSearch)
        }
      }

      const res = await fetch(url)
      if (!res.ok) throw new Error("Failed to fetch products")

      const data = await res.json()

      const newTotalPages = data.totalPages || 1

      if (page > newTotalPages) {
        setPage(newTotalPages)
        return
      }

      setProducts(data.products || [])
      setTotalPages(newTotalPages)
    } catch (err) {
      console.error("Error fetching products:", err)
      setProducts([])
    } finally {
      setLoading(false)
    }
  }, [page, selectedMerchant, debouncedSearch])

  /* =========================
     FETCH MERCHANTS (NEW)
  ========================= */
  const getMerchants = useCallback(async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/products/merchants`,
      )
      if (!res.ok) throw new Error("Failed to fetch merchants")

      const data = await res.json()
      setMerchants(data || [])
    } catch (err) {
      console.error("Error fetching merchants:", err)
      setMerchants([])
    }
  }, [])

  /* =========================
     FETCH CATEGORIES
  ========================= */
  const getCategories = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/categories`)
      if (!res.ok) throw new Error("Failed to fetch categories")

      const data = await res.json()
      setCategories(data || [])
    } catch (err) {
      console.error("Error fetching categories:", err)
      setCategories([])
    }
  }, [])

  useEffect(() => {
    getProducts()
    getCategories()
    getMerchants()
  }, [page, updated, getProducts, getCategories, getMerchants])

  //  IMPORTANT: reset page when merchant changes
  useEffect(() => {
    setPage(1)
  }, [selectedMerchant, search])

  /* =========================
     SELECT ALL LOGIC
  ========================= */
  const handleSelectAll = () => {
    const allIds = products.map((p) => p.product_id)
    setSelectedProducts(allIds)
  }

  const handleDeselectAll = () => {
    setSelectedProducts([])
  }

  const handleToggleSelectAll = () => {
    if (products.length === 0) return

    if (selectedProducts.length === products.length) {
      handleDeselectAll()
    } else {
      handleSelectAll()
    }
  }
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
    }, 800) // ⏱ delay in ms

    return () => clearTimeout(handler)
  }, [search])
  /* =========================
     BULK DELETE
  ========================= */
  const deleteSelectedProducts = async () => {
    if (selectedProducts.length === 0) {
      return alert("No products selected")
    }

    if (
      !window.confirm(`Delete ${selectedProducts.length} selected products?`)
    ) {
      return
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/products/bulk-delete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ ids: selectedProducts }),
        },
      )

      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.message || "Bulk delete failed")
      }

      const data = await res.json()

      alert(`Deleted ${data.deletedCount} products`)

      setSelectedProducts([])
      refresh()
    } catch (err) {
      console.error(err)
      alert(err.message || "Bulk delete failed")
    }
  }

  /* =========================
     LOADING
  ========================= */
  if (loading) {
    return (
      <div className="text-center py-5">
        <h2 className="lead">Loading products...</h2>
      </div>
    )
  }

  /* =========================
     EMPTY
  ========================= */
  if (products.length === 0) {
    return (
      <div className="AP_ProductList justify-content-center col-12 row">
        {/*  STILL SHOW FILTER EVEN IF EMPTY */}

        <div className="d-flex align-items-center">
          {/* 🔍 SEARCH INPUT */}
          <input
            type="text"
            className="form-control me-3"
            style={{ width: "250px" }}
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* 🏪 MERCHANT DROPDOWN */}
          <select
            className="form-select"
            style={{ width: "200px" }}
            value={selectedMerchant}
            onChange={(e) => setSelectedMerchant(e.target.value)}
          >
            <option value="">All Merchants</option>
            {merchants.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        <div className="text-center py-5">
          <p>No products found...</p>
          {/* Without these, an empty store has no way to add its first product */}
          <button
            className="btn btn-primary me-3"
            data-bs-toggle="modal"
            data-bs-target="#newProductModal"
          >
            Create new product
          </button>
          <button
            className="btn btn-primary"
            data-bs-toggle="modal"
            data-bs-target="#addProductsModal"
          >
            Import products from CSV
          </button>
        </div>

        <NewProductModal categories={categories} setUpdated={refresh} />
        <AddProductsModal categories={categories} setUpdated={refresh} />
      </div>
    )
  }

  /* =========================
     UI
  ========================= */
  return (
    <div className="AP_ProductList justify-content-center col-12 row px-4">
      {/*  FILTER BAR */}
      <div className="col-12 d-flex justify-content-between align-items-center mb-3  ">
        <div className="d-flex align-items-center">
          {/* 🔍 SEARCH INPUT */}
          <input
            type="text"
            className="form-control me-3"
            style={{ width: "250px" }}
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* MERCHANT DROPDOWN */}
          <select
            className="form-select"
            style={{ width: "200px" }}
            value={selectedMerchant}
            onChange={(e) => setSelectedMerchant(e.target.value)}
          >
            <option value="">All Merchants</option>
            {merchants.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        <div>
          <button
            className="btn btn-secondary me-2"
            onClick={handleToggleSelectAll}
          >
            {selectedProducts.length === products.length
              ? "Deselect All"
              : "Select All"}
          </button>

          <button
            className="btn btn-primary me-3"
            data-bs-toggle="modal"
            data-bs-target="#addProductsModal"
          >
            Import products from CSV
          </button>

          <button
            className="btn btn-primary me-3"
            data-bs-toggle="modal"
            data-bs-target="#newProductModal"
          >
            Create new product
          </button>

          <button
            className="btn btn-danger"
            disabled={selectedProducts.length === 0}
            onClick={deleteSelectedProducts}
          >
            Delete Selected ({selectedProducts.length})
          </button>
        </div>
      </div>

      {/* PRODUCTS GRID */}
      <div>
        <div className="row justify-content-center ">
          {products.map((product) => (
            <AdminProductCard
              key={product.product_id}
              productData={product}
              setUpdated={refresh}
              currentProduct={currentProduct}
              setCurrentProduct={setCurrentProduct}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
            />
          ))}
        </div>
      </div>

      {/* PAGINATION (UNCHANGED) */}
      <div className="d-flex justify-content-center mt-4">
        <nav>
          <ul className="pagination">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
              >
                Previous
              </button>
            </li>

            {(() => {
              const pages = []
              const windowSize = 5

              let start = Math.max(1, page - Math.floor(windowSize / 2))
              let end = start + windowSize - 1

              if (end > totalPages) {
                end = totalPages
                start = Math.max(1, end - windowSize + 1)
              }

              for (let i = start; i <= end; i++) {
                pages.push(
                  <li
                    key={i}
                    className={`page-item ${page === i ? "active" : ""}`}
                  >
                    <button className="page-link" onClick={() => setPage(i)}>
                      {i}
                    </button>
                  </li>,
                )
              }

              return pages
            })()}

            <li
              className={`page-item ${page === totalPages ? "disabled" : ""}`}
            >
              <button
                className="page-link"
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>

      {/* MODALS */}
      <NewProductModal categories={categories} setUpdated={refresh} />
      <AddProductsModal categories={categories} setUpdated={refresh} />

      {currentProduct && (
        <EditProductModal
          categories={categories}
          currentProduct={currentProduct}
          setCurrentProduct={setCurrentProduct}
          setUpdated={refresh}
        />
      )}
    </div>
  )
}

export default ProductsPanel
